import React from "react";
import { Link } from "react-router-dom";
import { Video, Scissors, Film, Share2, Building2, Users, UserPlus, Mail, Camera, Calendar, Upload, Bell, Smartphone, Monitor, MessageSquare, UserCircle } from "lucide-react";

const sections = [
  {
    title: "Video Features",
    items: [
      { icon: Video, title: "Full Game Recording", desc: "Professional gameday operators capture every moment of the game with high-quality video.", color: "cyan" },
      { icon: Scissors, title: "Clip Creation", desc: "Mark start and end points to create clips from any game. Save your favorite plays instantly.", color: "orange" },
      { icon: Film, title: "Highlight Tapes", desc: "Combine multiple clips into professional highlight reels perfect for college recruiting.", color: "green" },
      { icon: Share2, title: "Social Sharing", desc: "Share clips directly to Instagram, Twitter, TikTok and more with just one tap.", color: "purple" },
    ],
  },
  {
    title: "Team Management",
    items: [
      { icon: Building2, title: "League Administration", desc: "Manage multiple organizations, coordinate events, and oversee all league operations.", color: "cyan" },
      { icon: Users, title: "Organization Control", desc: "Organize teams within your organization, manage rosters, and control access permissions.", color: "orange" },
      { icon: UserPlus, title: "Team Rosters", desc: "Add players, coaches, and parents. Everyone gets appropriate access to their team's content.", color: "green" },
      { icon: Mail, title: "Invite System", desc: "Send invitations via email to add new members to your teams and organizations.", color: "purple" },
    ],
  },
  {
    title: "Operations",
    items: [
      { icon: Camera, title: "GDO Portal", desc: "Dedicated portal for Gameday Operators to manage assignments and upload game footage.", color: "cyan" },
      { icon: Calendar, title: "Game Scheduling", desc: "Assign operators to games, set locations, and manage the filming schedule.", color: "orange" },
      { icon: Upload, title: "Easy Upload", desc: "Simple upload process with progress tracking and automatic team notifications.", color: "green" },
      { icon: Bell, title: "Notifications", desc: "Teams are automatically notified when new game footage becomes available.", color: "purple" },
    ],
  },
  {
    title: "User Experience",
    items: [
      { icon: Smartphone, title: "Mobile First", desc: "Native iOS and Android apps with smooth performance and offline viewing capabilities.", color: "cyan" },
      { icon: Monitor, title: "Web Access", desc: "Access all features through any web browser - perfect for reviewing on bigger screens.", color: "orange" },
      { icon: MessageSquare, title: "Messaging", desc: "Built-in messaging system for coaches to communicate with players and parents.", color: "green" },
      { icon: UserCircle, title: "Player Profiles", desc: "Build a player profile with highlights and stats to share with recruiters.", color: "purple" },
    ],
  },
];

const FeaturesPage = () => (
  <div data-testid="features-page" className="page-gradient" style={{ paddingTop: 120 }}>
    {/* Hero */}
    <section style={{
      textAlign: 'center',
      padding: '40px 24px 64px',
      position: 'relative',
      zIndex: 1,
    }}>
      <p className="animate-fade-in-up" style={{
        color: 'var(--cyan)', fontSize: 13, fontWeight: 600,
        letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 20,
      }}>FEATURES</p>
      <h1 className="animate-fade-in-up stagger-1" style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: 'clamp(32px, 5vw, 54px)',
        fontWeight: 800,
        lineHeight: 1.1,
        marginBottom: 16,
      }}>Everything You Need<br />In One Platform</h1>
      <p className="animate-fade-in-up stagger-2" style={{
        color: 'var(--text-secondary)', fontSize: 16, maxWidth: 520,
        margin: '0 auto', lineHeight: 1.6,
      }}>
        From recording games to creating recruiting highlights, YSV provides all the tools your team needs.
      </p>
    </section>

    {/* Feature Sections */}
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 80px' }}>
      {sections.map((section, sIdx) => (
        <div key={section.title} data-testid={`feature-section-${sIdx}`} style={{ marginBottom: 64 }}>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: 24,
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: 32,
          }}>{section.title}</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
          }}>
            {section.items.map((item, i) => (
              <div key={item.title} className="glass-card animate-fade-in-up" style={{
                padding: 24,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
                opacity: 0,
                animationDelay: `${(sIdx * 4 + i) * 0.05}s`,
                animationFillMode: 'forwards',
              }}>
                <div className={`icon-box icon-box-${item.color}`}>
                  <item.icon size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{item.title}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* CTA */}
    <section style={{
      textAlign: 'center',
      padding: '0 24px 80px',
    }}>
      <div className="glow-line" style={{ maxWidth: 600, margin: '0 auto 48px' }} />
      <h2 style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: 'clamp(24px, 3vw, 36px)',
        fontWeight: 700,
        marginBottom: 12,
      }}>Ready to Transform Your Team's Video Experience?</h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 15, marginBottom: 32 }}>
        Start your free trial today.
      </p>
      <Link to="/get-started" data-testid="features-cta-btn" className="btn-primary" style={{
        textDecoration: 'none', padding: '14px 36px', fontSize: 15,
      }}>Start Free Trial</Link>
    </section>
  </div>
);

export default FeaturesPage;
